import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["animate", "appearAnimation", "callOnOrderChangeOnDragEnd", "delay", "description", "direction", "disableAllAnimations", "disableDropOnHover", "duration", "easing", "enterAnimation", "getPosition", "items", "leaveAnimation", "maintainContainerHeight", "onOrderChange", "onFinish", "onFinishAll", "onStart", "onStartAll", "staggerDelayBy", "staggerDurationBy"],
  _excluded2 = ["id"];
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import FlipMove from 'react-flip-move';
import { callIfPossible } from 'foundations-components/transitional/utils/functions';
import update from '../../utils/update';
// @ts-expect-error type UIDragListItem
import UIDragListItem from '../connected/UIDragListItem';
import { ScreenReaderOnly } from 'foundations-components/transitional/components/ScreenReaderOnly';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import styled, { useTheme } from 'foundations-theming/utils';
import { filterProps } from 'foundations-components/utils/filterProps';
import { useMapTokenPropsToCSS } from 'foundations-components/hooks/useMapTokenPropsToCSS';
import { deepMerge } from 'foundations-components/transitional/utils/deepMerge';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const defaultProps = {
  animate: true,
  delay: 0,
  description: 'Sortable list',
  // I18n cannot safely be accessed at module evaluation time
  disableAllAnimations: false,
  disableDropOnHover: false,
  direction: 'vertical',
  duration: 100,
  easing: 'cubic-bezier(.42, .0, .58, 1)',
  enterAnimation: 'elevator',
  getPosition: el => callIfPossible(el.getBoundingClientRect()),
  items: [],
  leaveAnimation: 'elevator',
  maintainContainerHeight: false,
  staggerDelayBy: 0,
  staggerDurationBy: 0,
  callOnOrderChangeOnDragEnd: false
};

/**
 * react-flip-move types
 */

const StyledOl = styled.ol.withConfig({
  displayName: "UISortableList__StyledOl"
})(({
  theme,
  $isHorizontal,
  $cssStyles
}) => {
  return Object.assign({
    // list-unstyled className styles
    listStyle: 'none',
    padding: theme.space['0'],
    margin: theme.space['0'],
    // flexDirection horizontal styles
    display: $isHorizontal ? 'flex' : undefined,
    justifyContent: $isHorizontal ? 'space-around' : undefined
  }, $cssStyles);
});
const UISortableList = _ref => {
  let {
      animate = defaultProps.animate,
      appearAnimation,
      callOnOrderChangeOnDragEnd = defaultProps.callOnOrderChangeOnDragEnd,
      delay = defaultProps.delay,
      description = defaultProps.description,
      direction = defaultProps.direction,
      disableAllAnimations = defaultProps.disableAllAnimations,
      disableDropOnHover = false,
      duration = defaultProps.duration,
      easing = defaultProps.easing,
      enterAnimation = defaultProps.enterAnimation,
      getPosition = defaultProps.getPosition,
      items: initialItems = defaultProps.items,
      leaveAnimation = defaultProps.leaveAnimation,
      maintainContainerHeight = defaultProps.maintainContainerHeight,
      onOrderChange,
      onFinish,
      onFinishAll,
      onStart,
      onStartAll,
      staggerDelayBy = defaultProps.staggerDelayBy,
      staggerDurationBy = defaultProps.staggerDurationBy
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = useTheme();
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const [items, setItems] = useState(() => initialItems);

  // Update our internal representation of `items` when the consumer
  // updates their list.
  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);
  const uniqueId = useUniqueId('UISortableList-');
  const instructionsId = `${uniqueId}-instructions`;
  const listRole = 'grid';
  const moveItem = useCallback((dragIndex, hoverIndex) => {
    const newItems = update(items, {
      $splice: [[dragIndex, 1], [hoverIndex, 0, items[dragIndex]]]
    });
    setItems(newItems);
    if (onOrderChange) {
      onOrderChange(newItems);
    }
  }, [items, onOrderChange]);
  const onHover = useCallback((dragIndex, hoverIndex) => {
    const newItems = update(items, {
      $splice: [[dragIndex, 1], [hoverIndex, 0, items[dragIndex]]]
    });
    setItems(newItems);
  }, [items]);
  const renderItem = useCallback((itemProps, index) => {
    const {
        id
      } = itemProps,
      passthroughProps = _objectWithoutPropertiesLoose(itemProps, _excluded2);
    return /*#__PURE__*/_jsx("li", {
      "aria-describedby": instructionsId,
      role: "gridcell",
      children: /*#__PURE__*/_jsx(UIDragListItem, Object.assign({}, passthroughProps, {
        callOnOrderChangeOnDragEnd: callOnOrderChangeOnDragEnd,
        direction: direction,
        disableDropOnHover: disableDropOnHover,
        index: index,
        id: id,
        moveItem: moveItem,
        onHover: onHover
      }))
    }, id);
  }, [direction, instructionsId, moveItem, disableDropOnHover, onHover, callOnOrderChangeOnDragEnd]);
  const defaultStyles = {
    listStyle: 'none',
    padding: theme.space['0'],
    margin: theme.space['0'],
    display: direction === 'horizontal' ? 'flex' : undefined,
    justifyContent: direction === 'horizontal' ? 'space-around' : undefined
  };
  const flipMoveStyles = deepMerge(defaultStyles, cssStyles);
  const renderedChildren = items.map((item, i) => renderItem(item, i));
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [!animate ?
    /*#__PURE__*/
    // @bend-expect trellis-tokenized (the spread operator in styled component is cssStyles)
    _jsx(StyledOl, Object.assign({}, domProps, {
      "aria-label": description || undefined,
      role: listRole,
      $isHorizontal: direction === 'horizontal',
      $cssStyles: cssStyles,
      children: renderedChildren
    })) :
    /*#__PURE__*/
    // @ts-expect-error doesn't match react-flip-move types
    // @bend-expect trellis-tokenized (FlipMove is a third party component)
    _jsx(FlipMove, Object.assign({}, domProps, {
      "data-test-id": "flip-move",
      appearAnimation: appearAnimation,
      delay: delay,
      disableAllAnimations: disableAllAnimations,
      duration: duration,
      easing: easing,
      enterAnimation: enterAnimation,
      getPosition: getPosition,
      leaveAnimation: leaveAnimation,
      maintainContainerHeight: maintainContainerHeight,
      onFinish: onFinish,
      onFinishAll: onFinishAll,
      onStart: onStart,
      onStartAll: onStartAll,
      staggerDurationBy: staggerDurationBy,
      staggerDelayBy: staggerDelayBy,
      role: listRole,
      typeName: "ol",
      style: flipMoveStyles // default styles & styles passed in from token props
      ,
      children: renderedChildren
    })), /*#__PURE__*/_jsx(ScreenReaderOnly, {
      id: instructionsId,
      children: I18n.text('UISortableList.directions')
    })]
  });
};
const FlipMoveAnimationPropType = PropTypes.oneOfType([PropTypes.oneOf(['elevator', 'fade', 'accordionVertical', 'accordionHorizontal', 'none']), PropTypes.bool, PropTypes.shape({
  from: PropTypes.object,
  to: PropTypes.object
})]);

/**
 * For detailed react-flip-move@2.9.15 type definitions, see
 * https://github.com/joshwcomeau/react-flip-move/blob/v2.9.15/typings/react-flip-move.d.ts
 */
export const FlipMovePropTypes = {
  appearAnimation: FlipMoveAnimationPropType,
  delay: PropTypes.number,
  disableAllAnimations: PropTypes.bool,
  duration: PropTypes.number,
  easing: PropTypes.string,
  enterAnimation: FlipMoveAnimationPropType,
  getPosition: PropTypes.func,
  leaveAnimation: FlipMoveAnimationPropType,
  maintainContainerHeight: PropTypes.bool,
  onFinish: PropTypes.func,
  onFinishAll: PropTypes.func,
  onStart: PropTypes.func,
  onStartAll: PropTypes.func,
  staggerDelayBy: PropTypes.number,
  staggerDurationBy: PropTypes.number
};
UISortableList.propTypes = Object.assign({}, FlipMovePropTypes, {
  /**
   * Whether drag animations should appear.
   */
  animate: PropTypes.bool,
  /**
   * When set to `true`, the `onOrderChange` callback will be called when the drag ends
   * instead of when a drop target detects an item being dropped over it.
   */
  callOnOrderChangeOnDragEnd: PropTypes.bool,
  /**
   * The description of this list provided to users of assistive technology.
   */
  description: PropTypes.string.isRequired,
  /**
   * The direction in which items can be dragged.
   */
  direction: PropTypes.oneOf(['horizontal', 'vertical']),
  /**
   * @deprecated This prop is deprecated. If you have a use case for it, please
   * reach out in #react-dnd-5-upgrade.
   */
  dragGroup: PropTypes.string,
  /**
   * Fires when the array of ordered items has changed.
   */
  onOrderChange: PropTypes.func,
  /**
   * An array of ordered item objects. Each item object should have an `id` and
   * `children`. All other props are passed through to the underlying `UIDragListItem`.
   * To fire custom logic when drags start or stop, pass a function to the `onDragChange`
   * callback which supplies `isDragging` as a parameter.
   */
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    children: PropTypes.node.isRequired
  })).isRequired
});

/* eslint-disable-next-line hubspot-dev/no-default-props-assignment */
UISortableList.defaultProps = defaultProps;
export default UISortableList;